import React from 'react';
import InsightList from '../components/InsightList';
import { BUCKET_META, SYNDROME_DEFINITION } from '../copy';
import { Figure } from '../components/Emphasis';
import { SEGMENT_ACCENT_BORDER, SEGMENT_VALUE_TEXT } from '../theme';
import {
  bucketCount, fmtCount, fmtPct, rankByPrevalence, share, shareAtLeast, tip, worstBandLabel,
} from '../format';

/**
 * KPI card per abnormality bucket. Built inline rather than with the DS `Card` atom: the design puts a
 * 1px gray-300 border, no resting shadow and 22/22/20 padding on these, where `Card` uses gray-200,
 * a shadow and uniform 24px.
 */
const BucketCard = ({ index, count, total }) => {
  const meta = BUCKET_META[index];

  return (
    <div
      data-tip={tip(meta.label, `${fmtCount(count)} of ${fmtCount(total)} employees`)}
      className={`rounded-ds-lg border border-gray-300 border-l-[3px] bg-white px-[22px] pb-ds-5 pt-[22px] ${SEGMENT_ACCENT_BORDER[index]}`}
    >
      <div
        className={`font-jetbrains-mono text-[38px] font-semibold leading-none tracking-[-0.02em] tabular-nums ${SEGMENT_VALUE_TEXT[index]}`}
      >
        {fmtPct(share(count, total))}
      </div>
      <div className='mt-[14px] text-[13px] font-semibold text-gray-900'>{meta.label}</div>
      <div className='mt-1 text-[12px] text-gray-600'>
        <span className='font-jetbrains-mono font-semibold text-gray-700'>{fmtCount(count)}</span>
        {` employees — ${meta.caption}`}
      </div>
    </div>
  );
};

const MetabolicAtAGlance = ({ data }) => {
  const total = data.cohort.total;
  const leader = rankByPrevalence(data.markers)[0];
  const caveated = data.markers.filter((marker) => marker.caveat);
  const worstBand = worstBandLabel(data.by_age);
  const male = data.by_gender.find((entry) => entry.gender === 'Male');
  const female = data.by_gender.find((entry) => entry.gender === 'Female');
  const maleTwoPlus = male ? shareAtLeast(male.buckets, 2) : 0;
  const femaleTwoPlus = female ? shareAtLeast(female.buckets, 2) : 0;
  const leadGender = maleTwoPlus >= femaleTwoPlus ? 'men' : 'women';

  const insights = [
    <>
      <Figure>{fmtPct(shareAtLeast(data.buckets, 1))}</Figure> of the workforce carries at least one metabolic
      abnormality; <Figure>{fmtPct(shareAtLeast(data.buckets, 2))}</Figure> already carry two or more.
    </>,
    leader ? (
      <>
        {leader.label} <Figure>({fmtPct(leader.prevalence)})</Figure> is the single most common issue — a broad,
        population-wide finding, not a niche one.
      </>
    ) : null,
    worstBand ? (
      <>
        Risk burden peaks in the <Figure>{worstBand}</Figure> band, and is carried more heavily by {leadGender}{' '}
        ({fmtPct(Math.max(maleTwoPlus, femaleTwoPlus))} at two or more, against{' '}
        {fmtPct(Math.min(maleTwoPlus, femaleTwoPlus))}).
      </>
    ) : null,
    caveated.length ? (
      <>
        {caveated.map((marker) => marker.label).join(' and ')} were tested in only a small share of the cohort —
        true syndrome prevalence is likely higher than{' '}
        <Figure>{fmtPct(share(bucketCount(data.buckets, 3), total))}</Figure>.
      </>
    ) : null,
  ];

  return (
    <div className='mt-ds-8'>
      <h3 className='m-0 mb-[6px] font-fig-extrabold text-[28px] font-bold leading-[1.18] tracking-[-0.01em] text-gray-900'>
        The workforce risk picture, at a glance
      </h3>
      <p className='m-0 mb-[22px] text-ts-md text-gray-600'>
        {`How many of ${data.cohort.markers} metabolic markers each employee is flagged on`}
      </p>

      <div className='grid grid-cols-1 gap-ds-4 sm:grid-cols-2 lg:grid-cols-4'>
        {BUCKET_META.map((_, index) => (
          <BucketCard key={index} index={index} count={bucketCount(data.buckets, index)} total={total} />
        ))}
      </div>

      <div className='mt-ds-5 grid grid-cols-1 gap-ds-5 lg:grid-cols-[1.35fr_1fr]'>
        <div className='rounded-ds-lg border border-gray-300 bg-white p-[26px]'>
          <div className='mb-ds-4 text-ts-xs font-semibold uppercase tracking-[0.12em] text-ds-brand'>
            What the numbers say
          </div>
          <InsightList items={insights} />
        </div>

        <div className='rounded-ds-xl border-l-4 border-ds-brand bg-ds-brand-lt p-[26px]'>
          <div className='text-ts-2xs font-semibold uppercase tracking-[0.10em] text-ds-brand-dk'>
            {SYNDROME_DEFINITION.eyebrow}
          </div>
          <h4 className='my-[10px] font-fig-extrabold text-[21px] font-bold leading-[1.25] text-gray-900'>
            {SYNDROME_DEFINITION.title}
          </h4>
          <p className='m-0 text-[14px] leading-[1.65] text-gray-800'>{SYNDROME_DEFINITION.body}</p>
        </div>
      </div>
    </div>
  );
};

export default MetabolicAtAGlance;
